import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Search, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";

import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";

export const Route = createFileRoute("/_authed/users/")({
  component: UsersList,
});

const PAGE = 50;

function UsersList() {
  const [q, setQ] = useState("");
  const [needle, setNeedle] = useState("");
  // Cursor stack: `cursors[i]` is the cursor that produced page i (null = first page).
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const cursor = cursors[cursors.length - 1];

  // Debounce the search box; a new needle restarts pagination.
  useEffect(() => {
    const t = setTimeout(() => {
      setNeedle(q.trim());
      setCursors([null]);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const query = useQuery({
    queryKey: ["admin", "users", needle, cursor],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/users", {
        params: { query: { cursor, limit: PAGE, q: needle || null } },
      });
      if (error) throw new Error("failed to load users");
      return data!;
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold">Users</h1>
          <p className="text-sm text-zinc-500">
            Every account, newest first — soft-deleted ones included.
          </p>
        </div>
        <div className="relative w-72">
          <Search className="pointer-events-none absolute left-3 top-3 size-4 text-zinc-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name, email or phone"
            className="pl-9"
            aria-label="Search users"
          />
        </div>
      </div>

      <Card>
        <CardBody className="p-0">
          {query.isPending ? (
            <p className="p-4 text-sm text-zinc-500">Loading…</p>
          ) : query.isError ? (
            <p className="p-4 text-sm text-red-600">Failed to load users.</p>
          ) : query.data.items.length === 0 ? (
            <p className="p-6 text-center text-sm text-zinc-500">
              {needle ? "Nobody matches that." : "No users yet."}
            </p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Name</TH>
                  <TH>Email</TH>
                  <TH>Phone</TH>
                  <TH>Joined</TH>
                  <TH>Status</TH>
                </TR>
              </THead>
              <TBody>
                {query.data.items.map((u) => (
                  <TR key={u.id} className={u.deleted_at ? "opacity-60" : undefined}>
                    <TD className="text-sm">
                      <span className="inline-flex items-center gap-1">
                        {u.display_name}
                        {u.is_superuser && (
                          <ShieldCheck
                            className="size-3.5 text-green-700"
                            aria-label="Superuser"
                          />
                        )}
                      </span>
                      <div className="font-mono text-[10px] text-zinc-400">{u.id}</div>
                    </TD>
                    <TD className="text-sm text-zinc-700">{u.email ?? "—"}</TD>
                    <TD className="text-sm text-zinc-700">{u.phone ?? "—"}</TD>
                    <TD className="text-xs text-zinc-600">
                      {new Date(u.created_at).toLocaleDateString()}
                    </TD>
                    <TD className="text-xs">
                      {u.deleted_at ? (
                        <span className="text-red-700">
                          deleted {new Date(u.deleted_at).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-zinc-600">active</span>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>

      <div className="flex items-center justify-between">
        <span className="text-xs text-zinc-500">
          Page {cursors.length}
          {query.data ? ` · ${query.data.items.length} shown` : ""}
        </span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            disabled={cursors.length <= 1 || query.isPending}
            onClick={() => setCursors((c) => c.slice(0, -1))}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={!query.data?.next_cursor || query.isPending}
            onClick={() =>
              query.data?.next_cursor &&
              setCursors((c) => [...c, query.data.next_cursor as string])
            }
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
